import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Battery, Shield, ShieldAlert, Lock, Leaf, MapPin, LogOut, ListTodo,
  BarChart3, AlertTriangle, FileDown, TrendingDown, Menu, Recycle, Award, ClipboardList,
} from 'lucide-react';

const GROUPS = [
  {
    heading: 'Summary',
    items: [
      { label: 'Dashboard', icon: BarChart3, path: '/dashboard' },
      { label: 'Map View', icon: MapPin, path: '/map' },
      { label: 'Alerts', icon: AlertTriangle, path: '/alerts' },
      { label: 'Tasks', icon: ListTodo, path: '/tasks' },
    ],
  },
  {
    heading: 'Performance',
    items: [
      { label: 'State of Health', icon: FileDown, path: '/reports/soh' },
      { label: 'State of Charge', icon: Battery, path: '/reports/soc' },
      { label: 'Degradation Rate', icon: TrendingDown, path: '/reports/degradation' },
      { label: 'Environmental', icon: Leaf, path: '/environmental' },
    ],
  },
  {
    heading: 'Cyber & Security',
    items: [
      { label: 'SCA Alerts', icon: Shield, path: '/sca-alerts' },
      { label: 'Cyber-Physical Security', icon: ShieldAlert, path: '/cyber-security' },
      { label: 'Physical Security', icon: Lock, path: '/physical-security' },
    ],
  },
  {
    heading: 'End of Life',
    items: [
      { label: 'Project Management', icon: ClipboardList, path: '/recycling/projects' },
      { label: 'Recycling & Disposal', icon: Recycle, path: '/recycling' },
      { label: 'Certification Report', icon: Award, path: '/recycling/certificates' },
    ],
  },
];

/**
 * Shared left-hand navigation, grouped into workstream sections so users can
 * jump section-to-section without returning to the dashboard first.
 */
export default function AppSidebar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(true);

  const isActive = (path) => {
    if (path === '/dashboard') return location.pathname === '/' || location.pathname.startsWith('/dashboard');
    if (path === '/recycling') return location.pathname === '/recycling';
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const logout = () => { localStorage.clear(); navigate('/login'); };

  return (
    <aside className={`${open ? 'w-64' : 'w-20'} bg-white border-r border-gray-200 transition-all duration-300 flex flex-col h-screen sticky top-0 shrink-0`}>
      <div className="p-4 border-b border-gray-200 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <img src="/bess-scan-mark.png" alt="BESS SCAN" className={`${open ? 'h-9' : 'h-8'} w-auto transition-all`} />
          {open && (
            <span className="text-lg font-extrabold tracking-tight truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <span className="text-[#0f2e4d]">BESS</span> <span className="text-red-600">SCAN</span>
            </span>
          )}
        </div>
        <Button variant="ghost" size="icon" className="shrink-0" onClick={() => setOpen(!open)} data-testid="sidebar-toggle">
          <Menu className="w-5 h-5" />
        </Button>
      </div>
      <nav className="flex-1 p-3 space-y-4 overflow-y-auto">
        {GROUPS.map((group) => (
          <div key={group.heading} data-testid={`nav-group-${group.heading.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
            {open ? (
              <div className="px-3 mb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-400">{group.heading}</div>
            ) : (
              <div className="mx-2 mb-1 border-t border-gray-100" />
            )}
            <div className="space-y-1">
              {group.items.map((it) => (
                <Button
                  key={it.path}
                  variant="ghost"
                  onClick={() => navigate(it.path)}
                  data-testid={`nav-${it.path.replace(/\//g, '-').replace(/^-/, '')}`}
                  className={`w-full justify-start ${isActive(it.path) ? 'bg-red-50 text-red-700 hover:bg-red-50 hover:text-red-700' : 'text-gray-700 hover:text-black hover:bg-gray-100'}`}
                  title={!open ? it.label : undefined}
                >
                  <it.icon className="w-5 h-5 shrink-0" />
                  {open && <span className="ml-3 text-left truncate">{it.label}</span>}
                </Button>
              ))}
            </div>
          </div>
        ))}
      </nav>
      <div className="p-3 border-t border-gray-200">
        <Button variant="ghost" onClick={logout} className="w-full justify-start text-gray-700 hover:text-black hover:bg-gray-100">
          <LogOut className="w-5 h-5 shrink-0" />
          {open && <span className="ml-3">Logout</span>}
        </Button>
      </div>
    </aside>
  );
}
